/**
 * AccountDeletionPort — the member account-erasure seam (033 US2, T029).
 *
 * `requestDeletion` triggers the prompt-erasure cascade for the
 * signed-in member (`member.myData.requestDeletion`,
 * contracts/my-data-api.md): no grace window, no pending-deletion
 * state — a resolved promise IS the completion confirmation (FR-006).
 * The explicit irreversibility confirmation happens in the UI
 * (`DeleteAccountConfirm`) BEFORE this port is called; the wire input's
 * `confirm: true` literal is supplied by the adapter.
 *
 * Adapters: the real tRPC adapter lands with the end-to-end wiring
 * (after the US1 my-data router merges); tests inject fakes. Follows
 * the MemberProfilePort port-hook-adapter convention.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 * Contract: specs/033-.../contracts/my-data-api.md +
 * contracts/deletion-and-pseudonymization.md.
 */

/**
 * Thrown by an adapter when the erasure machinery is not activated in
 * this environment (the API's P7 refusal while the audit-retention window
 * DL-009 has no authorized value — wire code
 * PRECONDITION_FAILED / `erasure-unavailable-unconfigured-retention`).
 * The UI renders a calm "not available yet", distinct from a transient
 * failure the member should retry.
 */
export class ErasureUnavailableError extends Error {
  constructor(message = 'account erasure is not activated in this environment') {
    super(message);
    this.name = 'ErasureUnavailableError';
    // Restore the prototype chain so `instanceof` stays reliable across
    // compile targets (matches ContactDetailsValidationError).
    Object.setPrototypeOf(this, ErasureUnavailableError.prototype);
  }
}

export interface AccountDeletionPort {
  /**
   * Run the erasure for the signed-in member. Resolves with the
   * completion confirmation once the cascade has committed. Rejects
   * with `ErasureUnavailableError` when erasure is not activated
   * (P7 refusal), or any other error on transient failure.
   */
  requestDeletion(): Promise<{ completedAt: string }>;
}
